import { message, Skeleton } from "antd";
import { useEffect } from "react";

import { BdsButton, BdsIconLinkSimple } from "@buildertrend/components";

import {
    type IEntityRecord,
    type IEntityRecordInsert,
} from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.types";

import { getJobPickerState } from "utilities/jobPicker/jobPicker";
import { useGetShareLink } from "utilities/sharing/share.utilities";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconCopy } from "commonComponents/btWrappers/BTIcon";
import { BTInputCopy } from "commonComponents/btWrappers/BTInputCopy/BTInputCopy";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";

import "./EntityShareButton.less";

export interface ISharingConfig {
    /**
     * Data necessary to create an IEntityRecord. By providing this prop, you are opting-in to
     * generating a sharable link to this modal.
     * Make sure to implement the correct types in EntityRecordSyncManager.
     */
    entity: IEntityRecordInsert;
    /**
     * Use this to customize how the entity record is fetched.
     * Must return an IEntityRecord type
     */
    overrideGetEntityRecord?: (data: IEntityRecordInsert) => Promise<IEntityRecord>;
}

export interface IBTShareButtonProps {
    sharing?: ISharingConfig;
}

export interface IBTShareButtonModalProps extends IBTShareButtonProps {
    sharing?: ISharingConfig;
    isVisible: boolean;
    onBeforeClose: () => void;
}

export const canShare = (sharing?: ISharingConfig): sharing is ISharingConfig => {
    if (sharing === undefined || getJobPickerState().isTemplateMode) {
        return false;
    }

    return sharing.entity.id > 0;
};

export const EntityShareButton: React.FunctionComponent<Pick<IBTShareButtonProps, "sharing">> = ({
    sharing,
}) => {
    const canShareEntity = canShare(sharing);
    const { data, error, refetch } = useGetShareLink(sharing);

    useEffect(() => {
        if (error) {
            console.error(error);
            void message.error("Unable to share at this time.");
        }
    }, [error]);

    let popoverContent: React.ReactNode;
    if (!data) {
        popoverContent = (
            <Skeleton.Input active size="small" className="EntityCopyButton-LoadingSkeleton" />
        );
    } else {
        popoverContent = (
            <BTInputCopy
                data-testid="shareUrl"
                id="shareUrl"
                value={data}
                onChange={() => {}}
                disabled
            />
        );
    }

    return (
        <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
            {canShareEntity && (
                <BTPopover
                    content={popoverContent}
                    placement="bottom"
                    arrowPointAtCenter
                    removeSpacing
                    widthType="unrestricted"
                    onVisibleChange={(visible) => {
                        if (visible && !data) {
                            void refetch();
                        }
                    }}
                    trigger="click"
                >
                    {/* We need to use title so as not to conflict with other popover on this element */}
                    {/* eslint-disable-next-line react/forbid-dom-props */}
                    <span title="Copy Share Link">
                        <BTButtonDeprecated
                            className="EntityCopyButton"
                            data-testid="entityCopyButton"
                            type="tertiary"
                            icon={<BTIconCopy size={20} />}
                        />
                    </span>
                </BTPopover>
            )}
        </div>
    );
};

export const EntityShareButtonBDS: React.FunctionComponent<
    Pick<IBTShareButtonProps, "sharing"> & { id: string }
> = ({ sharing, id }) => {
    const canShareEntity = canShare(sharing);
    const { data, error, refetch } = useGetShareLink(sharing);

    useEffect(() => {
        if (error) {
            console.error(error);
            void message.error("Unable to share at this time.");
        }
    }, [error]);

    let popoverContent: React.ReactNode;
    if (!data) {
        popoverContent = (
            <Skeleton.Input active size="small" className="EntityCopyButton-LoadingSkeleton" />
        );
    } else {
        popoverContent = (
            <BTInputCopy
                data-testid="shareUrl"
                id="shareUrl"
                value={data}
                onChange={() => {}}
                disabled
            />
        );
    }

    return (
        <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
            {canShareEntity && (
                <BTPopover
                    data-testid="entityShareButtonPopover"
                    content={popoverContent}
                    placement="bottomRight"
                    arrowPointAtCenter
                    removeSpacing
                    widthType="unrestricted"
                    onVisibleChange={(visible) => {
                        if (visible && !data) {
                            void refetch();
                        }
                    }}
                    trigger="click"
                >
                    <BdsButton
                        id={id}
                        testid="entityCopyButton"
                        displayType="tertiary"
                        ariaLabel="Copy share link"
                        icon={<BdsIconLinkSimple />}
                        tooltip="Copy share link"
                        value="copyShareLink"
                    />
                </BTPopover>
            )}
        </div>
    );
};

export const EntityShareRollupButtonBDS = ({
    sharing,
    id,
}: Pick<IBTShareButtonProps, "sharing"> & { id: string }) => {
    const canShareEntity = canShare(sharing);
    const { data, error, refetch } = useGetShareLink(sharing);

    useEffect(() => {
        if (error) {
            void message.error("Unable to share at this time.");
        }
    }, [error]);

    let popoverContent: React.ReactNode;
    if (!data) {
        popoverContent = (
            <Skeleton.Input active size="small" className="EntityCopyButton-LoadingSkeleton" />
        );
    } else {
        popoverContent = (
            <BTInputCopy
                data-testid="shareUrl"
                id="shareUrl"
                value={data}
                onChange={() => {}}
                disabled
            />
        );
    }

    return (
        <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
            {canShareEntity && (
                <BTPopover
                    data-testid="entityShareButtonPopover"
                    content={popoverContent}
                    placement="bottomRight"
                    arrowPointAtCenter
                    removeSpacing
                    widthType="unrestricted"
                    onVisibleChange={(visible) => {
                        if (visible && !data) {
                            void refetch();
                        }
                    }}
                    trigger="click"
                >
                    <BTPopover content="Copy share link">
                        <BTButton
                            id={id}
                            data-testid={id}
                            type="tertiary"
                            aria-label="Copy share link"
                            icon={<BdsIconLinkSimple />}
                        />
                    </BTPopover>
                </BTPopover>
            )}
        </div>
    );
};

export const EntityCopyLinkModal = ({
    isVisible,
    onBeforeClose,
    sharing,
}: IBTShareButtonModalProps) => {
    return (
        <BTModal
            data-testid="entity-link-share-modal"
            className="entity-link-share-modal"
            visible={isVisible}
            width="480px"
            beforeClose={() => onBeforeClose()}
            useModalLayout
        >
            <BTModalLayout
                title="Copy share link"
                modalConfig={{ parentRoute: "", beforeClose: () => onBeforeClose() }}
                modalHeaderClassName="entity-link-share-modal-header"
            >
                <BTLayoutContent>
                    <EntityCopyLinkModalContent sharing={sharing} />
                </BTLayoutContent>
            </BTModalLayout>
        </BTModal>
    );
};

export const EntityCopyLinkModalContent = ({ sharing }: Pick<IBTShareButtonProps, "sharing">) => {
    const { data, error, refetch } = useGetShareLink(sharing);

    useEffect(() => {
        if (error) {
            void message.error("Unable to share at this time.");
        }
    }, [error]);

    useEffect(() => {
        void refetch();
    }, [refetch]);

    let modalContent: React.ReactNode;
    if (!data) {
        modalContent = (
            <Skeleton.Input active size="small" className="EntityCopyButton-LoadingSkeleton" />
        );
    } else {
        modalContent = (
            <BTInputCopy
                data-testid="shareUrl"
                id="shareUrl"
                value={data}
                onChange={() => {}}
                disabled
            />
        );
    }

    return (
        <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
            {modalContent}
        </div>
    );
};
